import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import FlagLoader from "layouts/flagloader";

import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { hasPermission, mergeGrantedPermissions } from "../../../utils/granularPermissions";
import { useProjectPermissions } from "../projectPermissions";

const tableHeaderCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e9ecef",
  py: 0.75,
  px: 0.4,
  whiteSpace: "normal",
  lineHeight: 1.15,
  verticalAlign: "middle",
};

const tableBodyCellSx = {
  color: "#334155",
  fontSize: "10px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.4,
  px: 0.4,
  verticalAlign: "middle",
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const vendorHeaderCellSx = {
  ...tableHeaderCellSx,
  fontSize: "10px",
  padding: "4px 2px",
  lineHeight: 1.05,
  overflow: "hidden",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const vendorBodyCellSx = {
  ...tableBodyCellSx,
  fontSize: "9px",
  padding: "2.8px 2px",
  lineHeight: 1.1,
  overflow: "hidden",
  whiteSpace: "normal",
  wordBreak: "break-word",
  maxWidth: 0,
};

const userAgentCellSx = {
  ...vendorBodyCellSx,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "normal",
};

const compactInputSx = {
  width: "100%",
  height: "26px !important",
  minHeight: "26px !important",
  maxHeight: "26px !important",
  padding: "0 8px !important",
  "& input": {
    height: "26px !important",
    padding: "0 !important",
    fontSize: "10px !important",
    lineHeight: "26px !important",
  },
};

const compactFilterBoxSx = {
  width: 220,
  "--soft-select-height": "26px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-height": "26px",
  "--soft-select-value-px": "8px",
  "--soft-select-indicator-padding": "6px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-border-radius": "6px",
};

const countryStatusFilterSx = {
  ...compactFilterBoxSx,
  width: 115,
  minWidth: 105,
  flex: "1 1 115px",
};

const countryPersonFilterSx = {
  ...compactFilterBoxSx,
  width: 125,
  minWidth: 115,
  flex: "1 1 125px",
};

const linkTypeFilterSx = {
  ...compactFilterBoxSx,
  width: 200,
  minWidth: 115,
  maxWidth: 220,
  flex: "0 1 200px",
};

const projectCountryFilterSx = {
  ...compactFilterBoxSx,
  width: 145,
  minWidth: 130,
  flex: "1 1 145px",
};

const countryDateFilterSx = {
  width: 105,
  minWidth: 100,
  flex: "1 1 105px",
};

const countrySearchFilterSx = {
  width: 135,
  minWidth: 120,
  flex: "1 1 135px",
};

const countryFilterButtonSx = {
  minWidth: 72,
  height: 26,
  minHeight: 26,
  px: 1,
  py: 0,
  fontSize: "10px",
  textTransform: "none",
  whiteSpace: "nowrap",
  flex: "0 0 auto",
};

const statusOptions = [
  { value: "0", label: "Partial" },
  { value: "1", label: "Complete" },
  { value: "2", label: "Terminate" },
  { value: "3", label: "Quota Full" },
  { value: "4", label: "Security Terminate" },
  { value: "5", label: "Client Rejected" },
];

const statusLabels = statusOptions.reduce(
  (labels, option) => (option.value === "" ? labels : { ...labels, [option.value]: option.label }),
  {}
);

const linkTypeOptions = [
  { value: "live", label: "Live" },
  { value: "test", label: "Test" },
];

const normalizedLinkType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase() === "test"
    ? "test"
    : "live";

const linkTypeLabel = (value) => (normalizedLinkType(value) === "test" ? "Test" : "Live");

const clearedSelectValue = (option) => (Array.isArray(option) ? option : option ? [option] : []);
const selectedOptions = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const selectedValues = (value) =>
  selectedOptions(value)
    .map((option) => option?.value ?? option?.id ?? "")
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(String);
const hasSelectedValue = (value) => selectedValues(value).length > 0;
const matchesSelectedValue = (selected, value) => {
  const values = selectedValues(selected);
  return values.length === 0 || values.includes(String(value ?? ""));
};
const setMultiSelectParam = (params, key, value) => {
  const values = selectedValues(value);
  params.delete(key);
  params.delete(`${key}[]`);
  values.forEach((selectedValue) => params.append(`${key}[]`, selectedValue));
};

const textOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const summaryNumber = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const rowCountryLabel = (row) => {
  const countryName = row?.country_name || "";
  const countryCode = row?.country_code || "";
  if (countryName && countryCode) return `${countryName}(${countryCode})`;
  return countryName || countryCode || "-";
};

const countryOptionValue = (row) => String(row?.country_code || row?.country_name || "");

const countryOptionLabel = (row) => rowCountryLabel(row);

const projectCodeOptionValue = (row) => String(row?.project_code || "");

const projectCodeOptionLabel = (row) => String(row?.project_code || "");

const responseVendorLabel = (row) => String(row?.vendor_name || row?.vendor_code || "").trim();

const responseVendorValue = (row) =>
  String(row?.vendor_id || row?.supplier_id || row?.vendor_code || responseVendorLabel(row) || "");

const responseUserLabel = (row) =>
  String(row?.user_name || row?.user_email || row?.vendor_name || row?.vendor_code || "").trim();

const responseUserValue = (row) =>
  String(
    row?.user_id ||
      row?.user_email ||
      (row?.vendor_id ? `vendor-${row.vendor_id}` : "") ||
      responseUserLabel(row) ||
      ""
  );

const dateOnlyValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const studyLoiText = (createdAt, updatedAt) => {
  if (!createdAt || !updatedAt) return "-";
  const created = new Date(createdAt);
  const updated = new Date(updatedAt);
  if (Number.isNaN(created.getTime()) || Number.isNaN(updated.getTime())) return "-";

  const minutes = Math.abs(updated.getTime() - created.getTime()) / 60000;
  return Number.isFinite(minutes) ? minutes.toFixed(2) : "-";
};

const responseValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  return value;
};

const displayOsValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase() === "os x"
    ? "macOS"
    : responseValue(value);

const responseDateText = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const responseEndDateText = (row) =>
  row?.is_partial_response === true || String(row?.status ?? "") === "0"
    ? "N/A"
    : responseDateText(row?.updated_at);

const responseLoiText = (row) => {
  if (row?.is_partial_response === true || String(row?.status ?? "") === "0") return "N/A";

  let totalSeconds = Number(row?.user_loi_seconds);
  if (!Number.isFinite(totalSeconds)) {
    if (!row?.created_at || !row?.updated_at) return "N/A";
    const startedAt = new Date(row.created_at);
    const endedAt = new Date(row.updated_at);
    if (Number.isNaN(startedAt.getTime()) || Number.isNaN(endedAt.getTime())) return "N/A";
    totalSeconds = Math.max(0, Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000));
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const truncatedText = (value, maxLength = 64) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const uniqueOptions = (rows, valueGetter, labelGetter) => {
  const optionsByValue = new Map();

  rows.forEach((row) => {
    const value = valueGetter(row);
    const label = labelGetter(row);
    if (!value || !label) return;

    const key = String(value);
    if (!optionsByValue.has(key)) {
      optionsByValue.set(key, { value: key, label: String(label), name: String(label) });
    }
  });

  return Array.from(optionsByValue.values()).sort((first, second) =>
    first.label.localeCompare(second.label)
  );
};

const mergeFilterOptions = (options, selected = []) => {
  const byValue = new Map();

  [...(Array.isArray(options) ? options : []), ...selectedOptions(selected)].forEach((option) => {
    const value = option?.value ?? option?.id;
    const label = option?.label ?? option?.name ?? value;
    if (value === null || value === undefined || value === "" || !label) return;

    const normalized = {
      ...option,
      value: String(value),
      label: String(label),
      name: String(option?.name || label),
    };
    if (!byValue.has(normalized.value)) byValue.set(normalized.value, normalized);
  });

  return Array.from(byValue.values()).sort((first, second) =>
    first.label.localeCompare(second.label, undefined, { numeric: true, sensitivity: "base" })
  );
};

const exportFilenamePart = (value) =>
  String(value || "survey-responses")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function ProjectSurveyResponses({
  forceVendorProjectResponseRoute = false,
  forceAllResponseScope = false,
  forceSurveyResponsesReportRoute = false,
  PageLayout = null,
} = {}) {
  const { id, country: countryParam, vendorId, countryWinId: countryWinParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions = [], userPermission = [], user } = useAuth();
  const projectPermissions = useProjectPermissions(permissions, userPermission);
  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );
  const token = localStorage.getItem("authToken");
  const isVendorProjectResponseRoute =
    forceVendorProjectResponseRoute || location.pathname.startsWith("/vendor-projects");
  const isSurveyResponsesReportRoute =
    forceSurveyResponsesReportRoute || location.pathname.startsWith("/reports/survey-responses");
  const vendorResponseProjectId = forceAllResponseScope ? "" : id;
  const vendorProjectCan = useCallback(
    (...permissionKeys) => hasPermission(grantedPermissions, permissionKeys),
    [grantedPermissions]
  );
  const responsePermissionModule = isVendorProjectResponseRoute ? "vendor_projects" : "projects";
  const canViewResponseField = useCallback(
    (field) =>
      hasPermission(
        grantedPermissions,
        `${responsePermissionModule}.survey_responses.fields.${field}.view`
      ),
    [grantedPermissions, responsePermissionModule]
  );
  const canViewResponseFilter = useCallback(
    (filter) =>
      !isVendorProjectResponseRoute ||
      hasPermission(grantedPermissions, `vendor_projects.survey_responses.filters.${filter}.view`),
    [grantedPermissions, isVendorProjectResponseRoute]
  );
  const canViewSurveyResponsesReport = vendorProjectCan("reports.survey_responses.view");
  const canViewProjects = isSurveyResponsesReportRoute
    ? canViewSurveyResponsesReport
    : isVendorProjectResponseRoute
    ? vendorProjectCan("vendor_projects.pages.list.view")
    : projectPermissions.canViewPage;
  const canUseSurveyResponses = isSurveyResponsesReportRoute
    ? canViewSurveyResponsesReport
    : isVendorProjectResponseRoute
    ? vendorProjectCan("vendor_projects.actions.view_survey_responses")
    : projectPermissions.canAction("view_survey_responses");
  const responsePageScope = forceAllResponseScope
    ? "all"
    : isVendorProjectResponseRoute
    ? countryWinParam
      ? "country"
      : id
      ? "project"
      : "all"
    : vendorId
    ? "vendor"
    : countryParam
    ? "country"
    : "project";
  const canViewSurveyResponses = isSurveyResponsesReportRoute
    ? canViewSurveyResponsesReport
    : canUseSurveyResponses &&
      hasPermission(
        grantedPermissions,
        `${responsePermissionModule}.survey_responses.pages.${responsePageScope}.view`
      );
  const canSearchResponses = isVendorProjectResponseRoute
    ? canViewResponseFilter("search")
    : projectPermissions.canAction("search");
  const canExportSurveyResponses = isVendorProjectResponseRoute
    ? vendorProjectCan("vendor_projects.actions.export_survey_responses")
    : projectPermissions.canAction("export_survey_responses") ||
      projectPermissions.canAction("export");
  const canViewRevenueSummary =
    isVendorProjectResponseRoute &&
    vendorProjectCan("vendor_projects.survey_responses.summary.revenue.view");
  const canViewCompletesSummary =
    isVendorProjectResponseRoute &&
    vendorProjectCan("vendor_projects.survey_responses.summary.completes.view");
  const canViewIpMismatchSummary = isVendorProjectResponseRoute
    ? vendorProjectCan("vendor_projects.survey_responses.summary.ip_mismatches.view")
    : hasPermission(grantedPermissions, "projects.survey_responses.summary.ip_mismatches.view");
  const canViewProjectCode = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("project_code");
  const canViewClient = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("client");
  const canViewCountry = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("country");
  const canViewVendors = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("vendors");
  const canViewSourceType = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("source_type");
  const canViewSource = canViewVendors || canViewSourceType;
  const canViewLinkType = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("link_type");
  const canViewStatus = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("status");
  const canViewCreatedAt = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("created_at");
  const canViewUpdatedAt = isVendorProjectResponseRoute
    ? canViewSurveyResponses
    : projectPermissions.canField("updated_at");
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const surveyId = query.get("survey_id") || "";
  const detailId = query.get("detailId") || query.get("detail_id") || "";
  const countryWinId =
    countryWinParam || query.get("countryWinId") || query.get("country_win_id") || "";
  const sourceProjectTab = query.get("tab") || "live";
  const [loading, setLoading] = useState(true);
  const [bid, setBid] = useState(null);
  const [scope, setScope] = useState({});
  const [responseActorType, setResponseActorType] = useState("");
  const [responses, setResponses] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [ridOptionSearch, setRidOptionSearch] = useState("");
  const [pidOptionSearch, setPidOptionSearch] = useState("");
  const [ridOptionResults, setRidOptionResults] = useState([]);
  const [pidOptionResults, setPidOptionResults] = useState([]);
  const [ridOptionsLoading, setRidOptionsLoading] = useState(false);
  const [pidOptionsLoading, setPidOptionsLoading] = useState(false);
  const [responseSummary, setResponseSummary] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [pageInput, setPageInput] = useState("1");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState([]);
  const [vendorFilter, setVendorFilter] = useState([]);
  const [userFilter, setUserFilter] = useState([]);
  const [branchFilter, setBranchFilter] = useState([]);
  const [subBranchFilter, setSubBranchFilter] = useState([]);
  const [shiftFilter, setShiftFilter] = useState([]);
  const [countryFilter, setCountryFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [ridFilter, setRidFilter] = useState([]);
  const [pidFilter, setPidFilter] = useState([]);
  const [projectCodeFilter, setProjectCodeFilter] = useState([]);
  const [linkTypeFilter, setLinkTypeFilter] = useState([]);
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const responseRequestIdRef = useRef(0);

  const authHeaders = useMemo(
    () => ({
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const buildOptionScopeParams = useCallback(() => {
    const params = new URLSearchParams({ responseScope: responsePageScope });

    if (isSurveyResponsesReportRoute) params.set("survey_responses_report", "1");
    if (isSurveyResponsesReportRoute && surveyId) params.set("survey_id", surveyId);
    if (detailId) params.set("detailId", detailId);
    if (countryWinId) params.set("countryWinId", countryWinId);
    if (isVendorProjectResponseRoute && vendorResponseProjectId) {
      params.set("projectId", vendorResponseProjectId);
    }
    if (!isVendorProjectResponseRoute && vendorId) params.set("vendorId", vendorId);

    return params;
  }, [
    countryWinId,
    detailId,
    isSurveyResponsesReportRoute,
    isVendorProjectResponseRoute,
    responsePageScope,
    surveyId,
    vendorId,
    vendorResponseProjectId,
  ]);

  const scopeType = responsePageScope;
  const isProjectScope = scopeType === "project";
  const isVendorScope = scopeType === "vendor";
  const isCountryScope = scopeType === "country";
  const isVendorProjectAllScope = isVendorProjectResponseRoute && scopeType === "all";
  const isVendorActor =
    isVendorProjectResponseRoute &&
    String(responseActorType || user?.actor_type || "") === "vendor_contact";
  const hasSingleVendorBranch =
    Array.isArray(filterOptions.branches) && filterOptions.branches.length === 1;
  const isImplicitSingleVendorBranch = isVendorActor && hasSingleVendorBranch;
  const showVendorFilter =
    canViewSource &&
    canViewResponseFilter("vendor") &&
    (!isVendorProjectResponseRoute || !isVendorActor);
  const showUserFilter = isVendorProjectResponseRoute && canViewResponseFilter("user");
  const isCompactTableScope = isVendorScope || isCountryScope || isProjectScope;

  useEffect(() => {
    if (!isCompactTableScope) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [isCompactTableScope, searchInput]);

  const buildResponseParams = useCallback(
    (page = 1, perPage = 50) => {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        responseScope: responsePageScope,
      });

      if (isSurveyResponsesReportRoute) params.set("survey_responses_report", "1");
      if (isSurveyResponsesReportRoute && surveyId) params.set("survey_id", surveyId);

      if (detailId) params.set("detailId", detailId);
      if (countryWinId) params.set("countryWinId", countryWinId);
      if (isVendorProjectResponseRoute && vendorResponseProjectId) {
        params.set("projectId", vendorResponseProjectId);
      }
      if (!isVendorProjectResponseRoute && vendorId) params.set("vendorId", vendorId);
      setMultiSelectParam(params, "status", status);

      if (canSearchResponses && search.trim()) {
        params.set("summary_search", search.trim());
      }
      if (
        canViewResponseFilter("project_code") &&
        hasSelectedValue(projectCodeFilter)
      ) {
        setMultiSelectParam(params, "summary_project_code", projectCodeFilter);
      }
      if (showVendorFilter && hasSelectedValue(vendorFilter)) {
        setMultiSelectParam(params, "summary_vendor", vendorFilter);
      }
      if (showUserFilter && hasSelectedValue(userFilter)) {
        setMultiSelectParam(params, "summary_user", userFilter);
      }
      if (
        isVendorProjectResponseRoute &&
        canViewResponseFilter("branch") &&
        hasSelectedValue(branchFilter) &&
        !isImplicitSingleVendorBranch
      ) {
        setMultiSelectParam(params, "summary_branch", branchFilter);
      }
      if (
        isVendorProjectResponseRoute &&
        canViewResponseFilter("sub_branch") &&
        hasSelectedValue(subBranchFilter)
      ) {
        setMultiSelectParam(params, "summary_sub_branch", subBranchFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("shift") && hasSelectedValue(shiftFilter)) {
        setMultiSelectParam(params, "summary_shift", shiftFilter);
      }
      if (canViewResponseFilter("country") && hasSelectedValue(countryFilter)) {
        setMultiSelectParam(params, "summary_country", countryFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("client") && hasSelectedValue(clientFilter)) {
        setMultiSelectParam(params, "summary_client", clientFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("rid") && hasSelectedValue(ridFilter)) {
        setMultiSelectParam(params, "summary_rid", ridFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("pid") && hasSelectedValue(pidFilter)) {
        setMultiSelectParam(params, "summary_pid", pidFilter);
      }
      if (canViewResponseFilter("start_date") && startDateFilter) {
        params.set("summary_start_date", startDateFilter);
      }
      if (canViewResponseFilter("end_date") && endDateFilter) {
        params.set("summary_end_date", endDateFilter);
      }
      if (canViewResponseFilter("link_type") && hasSelectedValue(linkTypeFilter)) {
        setMultiSelectParam(params, "summary_link_type", linkTypeFilter);
      }

      return params;
    },
    [
      canSearchResponses,
      canViewResponseFilter,
      branchFilter,
      clientFilter,
      countryFilter,
      countryWinId,
      detailId,
      endDateFilter,
      id,
      isCountryScope,
      isImplicitSingleVendorBranch,
      isSurveyResponsesReportRoute,
      isVendorProjectResponseRoute,
      isVendorScope,
      linkTypeFilter,
      pidFilter,
      projectCodeFilter,
      ridFilter,
      responsePageScope,
      search,
      showUserFilter,
      showVendorFilter,
      startDateFilter,
      status,
      shiftFilter,
      subBranchFilter,
      surveyId,
      userFilter,
      vendorFilter,
      vendorId,
      vendorResponseProjectId,
    ]
  );

  const fetchResponses = useCallback(
    async (page = 1) => {
      const requestId = responseRequestIdRef.current + 1;
      responseRequestIdRef.current = requestId;

      if (!canViewProjects || !canViewSurveyResponses || !token) {
        setLoading(false);
        return;
      }

      if (!isVendorProjectResponseRoute && !id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const params = buildResponseParams(page, 50);

        const endpoint = isVendorProjectResponseRoute
          ? `${API_URL}/vendor-projects/survey-responses?${params}`
          : `${API_URL}/projects/${id}/survey-responses?${params}`;

        const response = await fetch(endpoint, {
          headers: authHeaders,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || "Failed to fetch survey responses");
        }

        if (requestId !== responseRequestIdRef.current) return;

        const responsePage = data.responses || {};
        setBid(data.bid || null);
        setScope(data.scope || {});
        setResponseActorType(data.actor_type || "");
        if (!isVendorProjectResponseRoute) {
          setFilterOptions(
            data.filter_options && typeof data.filter_options === "object"
              ? data.filter_options
              : {}
          );
        }
        setResponses(Array.isArray(responsePage.data) ? responsePage.data : []);
        setPagination({
          current_page: Number(responsePage.current_page || 1),
          last_page: Number(responsePage.last_page || 1),
          total: Number(responsePage.total || 0),
        });
        setPageInput(String(responsePage.current_page || 1));
      } catch (error) {
        if (requestId !== responseRequestIdRef.current) return;
        setResponses([]);
        setResponseSummary({});
        setPagination({ current_page: 1, last_page: 1, total: 0 });
        setPageInput("1");
      } finally {
        if (requestId === responseRequestIdRef.current) setLoading(false);
      }
    },
    [
      authHeaders,
      buildResponseParams,
      canViewProjects,
      canViewSurveyResponses,
      id,
      isVendorProjectResponseRoute,
      token,
    ]
  );

  useEffect(() => {
    fetchResponses(1);
  }, [fetchResponses]);

  useEffect(() => {
    if (!canViewProjects || !canViewSurveyResponses || !token || !isVendorProjectResponseRoute) {
      setFilterOptions({});
      return undefined;
    }

    const abortController = new AbortController();

    const loadFilterOptions = async () => {
      try {
        setFilterOptionsLoading(true);
        const params = buildOptionScopeParams();
        params.set("filter_options_only", "1");
        const response = await fetch(
          `${API_URL}/vendor-projects/survey-responses?${params.toString()}`,
          { headers: authHeaders, signal: abortController.signal }
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data?.success !== 1) {
          throw new Error(data?.message || "Failed to fetch filter options");
        }

        setFilterOptions(
          data.filter_options && typeof data.filter_options === "object" ? data.filter_options : {}
        );
      } catch (error) {
        if (error.name !== "AbortError") setFilterOptions({});
      } finally {
        if (!abortController.signal.aborted) setFilterOptionsLoading(false);
      }
    };

    loadFilterOptions();
    return () => abortController.abort();
  }, [
    authHeaders,
    buildOptionScopeParams,
    canViewProjects,
    canViewSurveyResponses,
    isVendorProjectResponseRoute,
    token,
  ]);

  useEffect(() => {
    if (!isVendorProjectResponseRoute || ridOptionSearch.trim().length < 2) {
      setRidOptionResults([]);
      setRidOptionsLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setRidOptionsLoading(true);
        const params = buildOptionScopeParams();
        params.set("option_search", "rid");
        params.set("option_search_query", ridOptionSearch.trim());
        const response = await fetch(
          `${API_URL}/vendor-projects/survey-responses?${params.toString()}`,
          { headers: authHeaders, signal: abortController.signal }
        );
        const data = await response.json().catch(() => ({}));
        setRidOptionResults(response.ok && Array.isArray(data?.options) ? data.options : []);
      } catch (error) {
        if (error.name !== "AbortError") setRidOptionResults([]);
      } finally {
        if (!abortController.signal.aborted) setRidOptionsLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [authHeaders, buildOptionScopeParams, isVendorProjectResponseRoute, ridOptionSearch]);

  useEffect(() => {
    if (!isVendorProjectResponseRoute || pidOptionSearch.trim().length < 2) {
      setPidOptionResults([]);
      setPidOptionsLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setPidOptionsLoading(true);
        const params = buildOptionScopeParams();
        params.set("option_search", "pid");
        params.set("option_search_query", pidOptionSearch.trim());
        const response = await fetch(
          `${API_URL}/vendor-projects/survey-responses?${params.toString()}`,
          { headers: authHeaders, signal: abortController.signal }
        );
        const data = await response.json().catch(() => ({}));
        setPidOptionResults(response.ok && Array.isArray(data?.options) ? data.options : []);
      } catch (error) {
        if (error.name !== "AbortError") setPidOptionResults([]);
      } finally {
        if (!abortController.signal.aborted) setPidOptionsLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [authHeaders, buildOptionScopeParams, isVendorProjectResponseRoute, pidOptionSearch]);

  useEffect(() => {
    if (
      !canViewProjects ||
      !canViewSurveyResponses ||
      (!canViewRevenueSummary && !canViewCompletesSummary && !canViewIpMismatchSummary) ||
      !token
    ) {
      setResponseSummary({});
      return undefined;
    }

    setResponseSummary({});
    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      const params = new URLSearchParams({
        summary_only: "1",
        responseScope: responsePageScope,
      });

      if (detailId) params.set("detailId", detailId);
      if (countryWinId) params.set("countryWinId", countryWinId);
      if (isVendorProjectResponseRoute && vendorResponseProjectId) {
        params.set("projectId", vendorResponseProjectId);
      }
      if (!isVendorProjectResponseRoute && vendorId) params.set("vendorId", vendorId);
      setMultiSelectParam(params, "status", status);

      const summarySearch = isCompactTableScope ? searchInput.trim() : search.trim();
      if (canSearchResponses && summarySearch) params.set("summary_search", summarySearch);
      if (
        canViewResponseFilter("project_code") &&
        hasSelectedValue(projectCodeFilter)
      ) {
        setMultiSelectParam(params, "summary_project_code", projectCodeFilter);
      }
      if (showVendorFilter && hasSelectedValue(vendorFilter)) {
        setMultiSelectParam(params, "summary_vendor", vendorFilter);
      }
      if (showUserFilter && hasSelectedValue(userFilter)) {
        setMultiSelectParam(params, "summary_user", userFilter);
      }
      if (
        isVendorProjectResponseRoute &&
        canViewResponseFilter("branch") &&
        hasSelectedValue(branchFilter) &&
        !isImplicitSingleVendorBranch
      ) {
        setMultiSelectParam(params, "summary_branch", branchFilter);
      }
      if (
        isVendorProjectResponseRoute &&
        canViewResponseFilter("sub_branch") &&
        hasSelectedValue(subBranchFilter)
      ) {
        setMultiSelectParam(params, "summary_sub_branch", subBranchFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("shift") && hasSelectedValue(shiftFilter)) {
        setMultiSelectParam(params, "summary_shift", shiftFilter);
      }
      if (canViewResponseFilter("country") && hasSelectedValue(countryFilter)) {
        setMultiSelectParam(params, "summary_country", countryFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("client") && hasSelectedValue(clientFilter)) {
        setMultiSelectParam(params, "summary_client", clientFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("rid") && hasSelectedValue(ridFilter)) {
        setMultiSelectParam(params, "summary_rid", ridFilter);
      }
      if (isVendorProjectResponseRoute && canViewResponseFilter("pid") && hasSelectedValue(pidFilter)) {
        setMultiSelectParam(params, "summary_pid", pidFilter);
      }
      if (canViewResponseFilter("start_date") && startDateFilter) {
        params.set("summary_start_date", startDateFilter);
      }
      if (canViewResponseFilter("end_date") && endDateFilter) {
        params.set("summary_end_date", endDateFilter);
      }
      if (canViewResponseFilter("link_type") && hasSelectedValue(linkTypeFilter)) {
        setMultiSelectParam(params, "summary_link_type", linkTypeFilter);
      }

      try {
        const endpoint = isVendorProjectResponseRoute
          ? `${API_URL}/vendor-projects/survey-responses?${params.toString()}`
          : `${API_URL}/projects/${id}/survey-responses?${params.toString()}`;
        const response = await fetch(endpoint, {
          headers: authHeaders,
          signal: abortController.signal,
        });
        const data = await response.json().catch(() => ({}));

        setResponseSummary(
          response.ok && data?.success === 1 && data?.summary && typeof data.summary === "object"
            ? data.summary
            : {}
        );
      } catch (error) {
        if (error.name !== "AbortError") setResponseSummary({});
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [
    authHeaders,
    branchFilter,
    canSearchResponses,
    canViewCompletesSummary,
    canViewIpMismatchSummary,
    canViewProjects,
    canViewResponseFilter,
    canViewRevenueSummary,
    canViewSurveyResponses,
    clientFilter,
    countryFilter,
    countryWinId,
    detailId,
    endDateFilter,
    id,
    isCompactTableScope,
    isImplicitSingleVendorBranch,
    isVendorProjectResponseRoute,
    linkTypeFilter,
    pidFilter,
    projectCodeFilter,
    ridFilter,
    responsePageScope,
    search,
    searchInput,
    showUserFilter,
    showVendorFilter,
    startDateFilter,
    status,
    shiftFilter,
    subBranchFilter,
    token,
    userFilter,
    vendorFilter,
    vendorId,
    vendorResponseProjectId,
  ]);

  const goBack = () => {
    if (isVendorProjectResponseRoute) {
      navigate(`/vendor-projects?tab=${encodeURIComponent(sourceProjectTab)}`);
      return;
    }

    if (scopeType === "vendor" || scopeType === "country") {
      const detailQuery = detailId ? `&detailId=${encodeURIComponent(detailId)}` : "";
      const vendorPath = `/projects/${id}/countries/${encodeURIComponent(countryParam)}/vendors`;

      navigate(`${vendorPath}?tab=${encodeURIComponent(sourceProjectTab)}${detailQuery}`);
      return;
    }

    navigate(`/projects/${id}/countries?tab=${encodeURIComponent(sourceProjectTab)}`);
  };

  const pageTitle = useMemo(() => {
    if (isVendorProjectResponseRoute) {
      if (scopeType === "country") return "Vendor Country Survey Responses";
      if (id) return "Vendor Project Survey Responses";
      return "Vendor Survey Responses";
    }

    if (scopeType === "vendor") return "Vendor Survey Responses";
    if (scopeType === "country") return "Country Survey Responses";
    return "Survey Responses";
  }, [id, isVendorProjectResponseRoute, scopeType]);

  const vendorName = useMemo(() => {
    const row = responses.find((response) => String(response.vendor_id || "") === String(vendorId));
    return row?.vendor_name || "";
  }, [responses, vendorId]);

  const projectCode = bid?.quest_code || scope?.project_code || "";
  const clientName =
    bid?.client_name || bid?.client?.name || bid?.client?.company_name || scope?.client || "";
  const countryLabel =
    scope?.country_name && scope?.country_code
      ? `${scope.country_name}(${scope.country_code})`
      : scope?.country_name || scope?.country_code || "";
  const scopeText = [
    canViewProjectCode && projectCode && `Project: ${projectCode}`,
    canViewClient && clientName && `Client: ${clientName}`,
    canViewCountry && countryLabel && `Country: ${countryLabel}`,
    isVendorScope && canViewVendors && `Vendor: ${textOrDash(vendorName)}`,
  ]
    .filter(Boolean)
    .join(" / ");
  const displayScopeText =
    scopeText || (isVendorProjectResponseRoute ? "All Vendor Survey Responses" : "");

  const vendorBranchOptions = useMemo(
    () =>
      mergeFilterOptions(
        (Array.isArray(filterOptions.branches) ? filterOptions.branches : []).map((branch) => ({
          value: String(branch.id),
          label: branch.name,
        })),
        branchFilter
      ),
    [branchFilter, filterOptions.branches]
  );
  useEffect(() => {
    if (!isVendorProjectResponseRoute || !hasSingleVendorBranch) return;

    const onlyBranch = vendorBranchOptions[0];
    if (!matchesSelectedValue(branchFilter, onlyBranch.value)) {
      setBranchFilter([onlyBranch]);
      setSubBranchFilter([]);
      setShiftFilter([]);
      setUserFilter([]);
    }
  }, [branchFilter, hasSingleVendorBranch, isVendorProjectResponseRoute, vendorBranchOptions]);

  const vendorSubBranchOptions = useMemo(() => {
    const selectedBranches = selectedValues(branchFilter);
    const options = (Array.isArray(filterOptions.sub_branches) ? filterOptions.sub_branches : [])
      .filter(
        (subBranch) =>
          !selectedBranches.length || selectedBranches.includes(String(subBranch.branch_id))
      )
      .map((subBranch) => ({ value: String(subBranch.id), label: subBranch.name }));

    return mergeFilterOptions(options, subBranchFilter);
  }, [branchFilter, filterOptions.sub_branches, subBranchFilter]);

  const vendorShiftOptions = useMemo(() => {
    const selectedSubBranches = selectedValues(subBranchFilter);
    const options = (Array.isArray(filterOptions.shifts) ? filterOptions.shifts : [])
      .filter(
        (shift) =>
          !selectedSubBranches.length || selectedSubBranches.includes(String(shift.subbranch_id))
      )
      .map((shift) => {
        const shiftType = String(shift.type || "Shift").replace(/^./, (letter) =>
          letter.toUpperCase()
        );
        const shiftTime =
          shift.start_time && shift.end_time ? ` (${shift.start_time} - ${shift.end_time})` : "";

        return {
          value: String(shift.id),
          label: `${shiftType}${shiftTime}`,
        };
      });

    return mergeFilterOptions(options, shiftFilter);
  }, [filterOptions.shifts, shiftFilter, subBranchFilter]);

  const vendorUserOptions = useMemo(() => {
    const selectedShifts = selectedValues(shiftFilter);
    const options = (Array.isArray(filterOptions.users) ? filterOptions.users : [])
      .filter(
        (option) =>
          !selectedShifts.length ||
          (option.shift_ids || []).some((id) => selectedShifts.includes(String(id)))
      )
      .map((option) => ({
        value: String(option.id),
        label: option.name,
      }));

    return mergeFilterOptions(options, userFilter);
  }, [filterOptions.users, shiftFilter, userFilter]);

  const vendorCountryOptions = useMemo(
    () => mergeFilterOptions(filterOptions.countries, countryFilter),
    [countryFilter, filterOptions.countries]
  );
  const vendorClientOptions = useMemo(
    () => mergeFilterOptions(filterOptions.clients, clientFilter),
    [clientFilter, filterOptions.clients]
  );
  const vendorFilterOptions = useMemo(
    () => mergeFilterOptions(filterOptions.vendors, vendorFilter),
    [filterOptions.vendors, vendorFilter]
  );
  const ridFilterOptions = useMemo(
    () => mergeFilterOptions(ridOptionResults, ridFilter),
    [ridFilter, ridOptionResults]
  );
  const pidFilterOptions = useMemo(
    () => mergeFilterOptions(pidOptionResults, pidFilter),
    [pidFilter, pidOptionResults]
  );

  const countryVendorOptions = useMemo(
    () => {
      if (isVendorProjectResponseRoute) return vendorFilterOptions;
      if (!isCountryScope && !isProjectScope && !isVendorProjectAllScope) return [];

      const options = Array.isArray(filterOptions.vendors)
        ? filterOptions.vendors
        : uniqueOptions(
            responses.filter((row) => responseVendorLabel(row)),
            responseVendorValue,
            responseVendorLabel
          );

      return mergeFilterOptions(options, vendorFilter);
    },
    [
      filterOptions.vendors,
      isCountryScope,
      isProjectScope,
      isVendorProjectAllScope,
      isVendorProjectResponseRoute,
      responses,
      vendorFilter,
      vendorFilterOptions,
    ]
  );

  const countryUserOptions = useMemo(() => {
    if (isVendorProjectResponseRoute) return vendorUserOptions;

    const selectedVendorValues = showVendorFilter ? selectedValues(vendorFilter) : [];
    const userRows = selectedVendorValues.length
      ? responses.filter((row) => selectedVendorValues.includes(responseVendorValue(row)))
      : responses;

    return isCountryScope || isProjectScope || isVendorProjectAllScope
      ? uniqueOptions(
          userRows.filter((row) => responseUserLabel(row)),
          responseUserValue,
          responseUserLabel
        )
      : [];
  }, [
    isCountryScope,
    isProjectScope,
    isVendorProjectAllScope,
    responses,
    isVendorProjectResponseRoute,
    showVendorFilter,
    vendorFilter,
    vendorUserOptions,
  ]);

  const handleVendorFilterChange = (option, actionMeta) => {
    setVendorFilter(clearedSelectValue(option, actionMeta));
    setUserFilter([]);
  };

  const handleBranchFilterChange = (option, actionMeta) => {
    if (hasSingleVendorBranch && !selectedOptions(option).length) return;
    setBranchFilter(clearedSelectValue(option, actionMeta));
    setSubBranchFilter([]);
    setShiftFilter([]);
    setUserFilter([]);
  };

  const handleSubBranchFilterChange = (option, actionMeta) => {
    setSubBranchFilter(clearedSelectValue(option, actionMeta));
    setShiftFilter([]);
    setUserFilter([]);
  };

  const handleShiftFilterChange = (option, actionMeta) => {
    setShiftFilter(clearedSelectValue(option, actionMeta));
    setUserFilter([]);
  };

  const projectCountryOptions = useMemo(
    () =>
      isProjectScope
        ? mergeFilterOptions(
            Array.isArray(filterOptions.countries)
              ? filterOptions.countries
              : uniqueOptions(
                  responses.filter((row) => countryOptionValue(row)),
                  countryOptionValue,
                  countryOptionLabel
                ),
            countryFilter
          )
        : [],
    [countryFilter, filterOptions.countries, isProjectScope, responses]
  );

  const projectCodeOptions = useMemo(
    () =>
      mergeFilterOptions(
        Array.isArray(filterOptions.project_codes)
          ? filterOptions.project_codes
          : uniqueOptions(
              responses.filter((row) => projectCodeOptionValue(row)),
              projectCodeOptionValue,
              projectCodeOptionLabel
            ),
        projectCodeFilter
      ),
    [filterOptions.project_codes, projectCodeFilter, responses]
  );

  const visibleResponses = useMemo(() => {
    // Vendor Project filters are applied by the backend before pagination.
    // Do not filter the current page a second time, otherwise the UI can differ
    // from the all-pages export for fields handled only by the backend search.
    if (isVendorProjectResponseRoute) return responses;

    if (isCountryScope) {
      const searchTerm = searchInput.trim().toLowerCase();
      const selectedProjectCodeValues = selectedValues(projectCodeFilter);
      const selectedLinkTypes = selectedValues(linkTypeFilter);

      return responses.filter((row) => {
        const rowVendorValue = responseVendorValue(row);
        const rowUserValue = responseUserValue(row);
        const selectedVendorValues = showVendorFilter ? selectedValues(vendorFilter) : [];
        const selectedUserValues = showUserFilter ? selectedValues(userFilter) : [];

        if (selectedProjectCodeValues.length && !selectedProjectCodeValues.includes(projectCodeOptionValue(row))) {
          return false;
        }
        if (selectedLinkTypes.length && !selectedLinkTypes.includes(normalizedLinkType(row.link_type)))
          return false;

        if (selectedVendorValues.length && !selectedVendorValues.includes(rowVendorValue)) return false;
        if (selectedUserValues.length && !selectedUserValues.includes(rowUserValue)) return false;

        const createdDate = dateOnlyValue(row.created_at);
        if (startDateFilter && (!createdDate || createdDate < startDateFilter)) return false;
        if (endDateFilter && (!createdDate || createdDate > endDateFilter)) return false;
        if (!searchTerm) return true;

        const haystack = [
          linkTypeLabel(row.link_type),
          row.project_code,
          row.project_name,
          responseVendorLabel(row),
          responseUserLabel(row),
          row.pid,
          row.rid,
          statusLabels[String(row.status)] || row.status,
          row.cpi,
          studyLoiText(row.created_at, row.updated_at),
          row.overall_quota,
          row.country_quota,
          row.vendor_quota,
          row.start_ip,
          row.callback_ip,
          row.callback_country_code,
          row.device,
          row.browser,
          row.os,
          row.user_agent,
          row.callback_user_agent,
          row.created_at,
          row.updated_at,
        ]
          .map((value) => String(value ?? "").toLowerCase())
          .join(" ");

        return haystack.includes(searchTerm);
      });
    }

    if (isProjectScope) {
      const searchTerm = searchInput.trim().toLowerCase();
      const selectedCountryValues = selectedValues(countryFilter);
      const selectedProjectCodeValues = selectedValues(projectCodeFilter);
      const selectedLinkTypes = selectedValues(linkTypeFilter);

      return responses.filter((row) => {
        const rowVendorValue = responseVendorValue(row);
        const rowUserValue = responseUserValue(row);
        const selectedVendorValues = showVendorFilter ? selectedValues(vendorFilter) : [];
        const selectedUserValues = showUserFilter ? selectedValues(userFilter) : [];

        if (selectedCountryValues.length && !selectedCountryValues.includes(countryOptionValue(row))) return false;
        if (selectedProjectCodeValues.length && !selectedProjectCodeValues.includes(projectCodeOptionValue(row))) {
          return false;
        }
        if (selectedLinkTypes.length && !selectedLinkTypes.includes(normalizedLinkType(row.link_type)))
          return false;

        if (selectedVendorValues.length && !selectedVendorValues.includes(rowVendorValue)) return false;
        if (selectedUserValues.length && !selectedUserValues.includes(rowUserValue)) return false;

        const createdDate = dateOnlyValue(row.created_at);
        if (startDateFilter && (!createdDate || createdDate < startDateFilter)) return false;
        if (endDateFilter && (!createdDate || createdDate > endDateFilter)) return false;
        if (!searchTerm) return true;

        const haystack = [
          linkTypeLabel(row.link_type),
          row.project_code,
          row.project_name,
          rowCountryLabel(row),
          responseVendorLabel(row),
          responseUserLabel(row),
          row.pid,
          row.rid,
          statusLabels[String(row.status)] || row.status,
          row.cpi,
          studyLoiText(row.created_at, row.updated_at),
          row.overall_quota,
          row.country_quota,
          row.vendor_quota,
          row.start_ip,
          row.callback_ip,
          row.callback_country_code,
          row.device,
          row.browser,
          row.os,
          row.user_agent,
          row.callback_user_agent,
          row.created_at,
          row.updated_at,
        ]
          .map((value) => String(value ?? "").toLowerCase())
          .join(" ");

        return haystack.includes(searchTerm);
      });
    }

    if (isVendorProjectAllScope) {
      const selectedVendorValues = showVendorFilter ? selectedValues(vendorFilter) : [];
      const selectedUserValues = showUserFilter ? selectedValues(userFilter) : [];

      return responses.filter((row) => {
        if (selectedVendorValues.length && !selectedVendorValues.includes(responseVendorValue(row))) return false;
        if (selectedUserValues.length && !selectedUserValues.includes(responseUserValue(row))) return false;
        return true;
      });
    }

    if (!isVendorScope) return responses;

    const searchTerm = searchInput.trim().toLowerCase();
    const selectedProjectCodeValues = selectedValues(projectCodeFilter);
    const selectedLinkTypes = selectedValues(linkTypeFilter);

    return responses.filter((row) => {
      if (selectedProjectCodeValues.length && !selectedProjectCodeValues.includes(projectCodeOptionValue(row))) {
        return false;
      }
      if (selectedLinkTypes.length && !selectedLinkTypes.includes(normalizedLinkType(row.link_type))) return false;

      const createdDate = dateOnlyValue(row.created_at);
      if (startDateFilter && (!createdDate || createdDate < startDateFilter)) return false;
      if (endDateFilter && (!createdDate || createdDate > endDateFilter)) return false;
      if (!searchTerm) return true;

      const haystack = [
        linkTypeLabel(row.link_type),
        row.project_code,
        row.project_name,
        row.pid,
        row.rid,
        statusLabels[String(row.status)] || row.status,
        row.cpi,
        row.loi,
        studyLoiText(row.created_at, row.updated_at),
        row.ir,
        row.overall_quota,
        row.country_quota,
        row.vendor_quota,
        row.start_ip,
        row.callback_ip,
        row.callback_country_code,
        row.device,
        row.browser,
        row.os,
        row.user_agent,
        row.callback_user_agent,
        row.created_at,
        row.updated_at,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");

      return haystack.includes(searchTerm);
    });
  }, [
    endDateFilter,
    countryFilter,
    isCountryScope,
    isProjectScope,
    isVendorScope,
    isVendorProjectAllScope,
    isVendorProjectResponseRoute,
    linkTypeFilter,
    projectCodeFilter,
    responses,
    searchInput,
    showUserFilter,
    showVendorFilter,
    startDateFilter,
    userFilter,
    vendorFilter,
  ]);

  useEffect(() => {
    if (!isCompactTableScope) return;
    if (isVendorProjectResponseRoute) return;

    if (
      isProjectScope &&
      hasSelectedValue(countryFilter) &&
      selectedValues(countryFilter).some((value) => !projectCountryOptions.some((option) => String(option.value) === value))
    ) {
      setCountryFilter([]);
    }

    if (
      hasSelectedValue(projectCodeFilter) &&
      selectedValues(projectCodeFilter).some((value) => !projectCodeOptions.some((option) => String(option.value) === value))
    ) {
      setProjectCodeFilter([]);
    }

    if (
      hasSelectedValue(vendorFilter) &&
      selectedValues(vendorFilter).some((value) => !countryVendorOptions.some((option) => String(option.value) === value))
    ) {
      setVendorFilter([]);
    }

    if (
      hasSelectedValue(userFilter) &&
      selectedValues(userFilter).some((value) => !countryUserOptions.some((option) => String(option.value) === value))
    ) {
      setUserFilter([]);
    }
  }, [
    countryFilter,
    countryUserOptions,
    countryVendorOptions,
    isCountryScope,
    isCompactTableScope,
    isProjectScope,
    isVendorProjectResponseRoute,
    projectCountryOptions,
    projectCodeFilter,
    projectCodeOptions,
    userFilter,
    vendorFilter,
  ]);

  const hasLocalPagination = false;
  const paginationStart = visibleResponses.length
    ? hasLocalPagination
      ? 1
      : (pagination.current_page - 1) * 50 + 1
    : 0;
  const paginationEnd = visibleResponses.length
    ? hasLocalPagination
      ? visibleResponses.length
      : (pagination.current_page - 1) * 50 + visibleResponses.length
    : 0;
  const paginationTotal = hasLocalPagination ? visibleResponses.length : pagination.total;

  const applyFilters = () => {
    setSearch(searchInput);
  };

  const goToResponsePage = (value) => {
    const page = Math.max(1, Math.min(pagination.last_page || 1, Number(value) || 1));
    setPageInput(String(page));
    if (page !== pagination.current_page) fetchResponses(page);
  };

  const responseColumns = [
    canViewResponseField("project_id")
      ? {
          key: "project_id",
          label: "Project ID",
          render: (row) => responseValue(row.quest_code || bid?.quest_code || scope?.project_code),
        }
      : null,
    canViewResponseField("link_type")
      ? { key: "link_type", label: "Link Type", render: (row) => linkTypeLabel(row.link_type) }
      : null,
    canViewResponseField("project_type")
      ? {
          key: "project_type",
          label: "Project Type",
          render: (row) =>
            responseValue(row.project_type || bid?.project_type_option || bid?.project_type),
        }
      : null,
    canViewResponseField("project_name")
      ? {
          key: "project_name",
          label: "Project Name",
          render: (row) => responseValue(row.project_name),
        }
      : null,
    canViewResponseField("client_name")
      ? {
          key: "client_name",
          label: "Client Name",
          render: (row) => responseValue(row.client_name || clientName),
        }
      : null,
    canViewResponseField("country_iso")
      ? {
          key: "country_iso",
          label: "Country ISO",
          render: (row) => responseValue(row.country_code || row.geo_country_code),
        }
      : null,
    canViewResponseField("pid")
      ? { key: "pid", label: "PID", render: (row) => responseValue(row.pid) }
      : null,
    canViewResponseField("rid")
      ? { key: "rid", label: "RID", render: (row) => responseValue(row.rid) }
      : null,
    canViewResponseField("status")
      ? {
          key: "status",
          label: "Status",
          render: (row) => statusLabels[String(row.status)] || responseValue(row.status),
        }
      : null,
    isVendorProjectResponseRoute && canViewResponseField("final_status")
      ? {
          key: "final_status",
          label: "Final Status",
          render: (row) => {
            const value = row.final_status;
            return value === null || value === undefined || String(value).trim() === ""
              ? "Pending"
              : value;
          },
        }
      : null,
    canViewResponseField("entry_ip")
      ? {
          key: "entry_ip",
          label: "Entry IP Address",
          render: (row) => responseValue(row.start_ip),
        }
      : null,
    canViewResponseField("exit_ip")
      ? {
          key: "exit_ip",
          label: "Exit IP Address",
          render: (row) => responseValue(row.callback_ip),
        }
      : null,
    canViewResponseField("country")
      ? {
          key: "country",
          label: "Country",
          render: (row) => responseValue(row.geo_country_code),
        }
      : null,
    canViewResponseField("region")
      ? { key: "region", label: "Region", render: (row) => responseValue(row.region) }
      : null,
    canViewResponseField("city")
      ? { key: "city", label: "City", render: (row) => responseValue(row.city) }
      : null,
    canViewResponseField("device_type")
      ? {
          key: "device_type",
          label: "Device Type",
          render: (row) => responseValue(row.device),
        }
      : null,
    canViewResponseField("browser")
      ? { key: "browser", label: "Browser", render: (row) => responseValue(row.browser) }
      : null,
    canViewResponseField("os")
      ? { key: "os", label: "OS", render: (row) => displayOsValue(row.os) }
      : null,
    canViewResponseField("user_agent")
      ? {
          key: "user_agent",
          label: "User Agent",
          render: (row) => responseValue(row.user_agent || row.callback_user_agent),
          title: (row) => responseValue(row.user_agent || row.callback_user_agent),
          truncate: true,
        }
      : null,
    canViewResponseField("client_cpi")
      ? {
          key: "client_cpi",
          label: "Client CPI",
          render: (row) => responseValue(row.client_cpi),
        }
      : null,
    canViewResponseField("vendor_cpi")
      ? {
          key: "vendor_cpi",
          label: "Vendor CPI",
          render: (row) => responseValue(row.vendor_cpi),
        }
      : null,
    canViewResponseField("vendor") && (!isVendorProjectResponseRoute || !isVendorActor)
      ? {
          key: "vendor",
          label: "Vendor",
          render: (row) => responseValue(responseVendorLabel(row)),
        }
      : null,
    isVendorProjectResponseRoute && canViewResponseField("user")
      ? {
          key: "user",
          label: "User",
          render: (row) => responseValue(responseUserLabel(row)),
        }
      : null,
    canViewResponseField("start_date")
      ? {
          key: "start_date",
          label: "Start Date",
          render: (row) => responseDateText(row.created_at),
        }
      : null,
    canViewResponseField("end_date")
      ? {
          key: "end_date",
          label: "End Date",
          render: (row) => responseEndDateText(row),
        }
      : null,
    canViewResponseField("user_loi")
      ? { key: "user_loi", label: "User LOI", render: (row) => responseLoiText(row) }
      : null,
  ].filter(Boolean);

  const exportResponses = async (scopeLabel) => {
    if (!canExportSurveyResponses || !responseColumns.length) return;

    try {
      setExportLoading(true);

      // The table is paginated for display only. Export every matching response
      // from the same project/country/vendor scope and active filters.
      const exportResponseRows = [];
      let exportPage = 1;
      let hasMorePages = true;

      do {
        const params = buildResponseParams(exportPage, 2000);
        if (isVendorProjectResponseRoute) params.set("export_mode", "1");

        const endpoint = isVendorProjectResponseRoute
          ? `${API_URL}/vendor-projects/survey-responses?${params.toString()}`
          : `${API_URL}/projects/${id}/survey-responses?${params.toString()}`;
        const response = await fetch(endpoint, { headers: authHeaders });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || "Failed to export survey responses");
        }

        const responsePage = data.responses || {};
        exportResponseRows.push(...(Array.isArray(responsePage.data) ? responsePage.data : []));
        hasMorePages = isVendorProjectResponseRoute
          ? Boolean(responsePage.next_page_url)
          : exportPage < Math.max(1, Number(responsePage.last_page || 1));
        exportPage += 1;
      } while (hasMorePages);

      const exportColumns = responseColumns;
      const headers = exportColumns.map((column) => column.label);
      const rows = exportResponseRows.map((row) =>
        exportColumns.map((column) => textOrDash(column.render(row)))
      );

      const XLSX = await import("xlsx-js-style");
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      exportResponseRows.forEach((row, rowIndex) => {
        if (!row.ip_mismatch) return;

        exportColumns.forEach((column, columnIndex) => {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex });
          if (!worksheet[cellAddress]) return;

          worksheet[cellAddress].s = {
            fill: {
              patternType: "solid",
              fgColor: { rgb: "FFF3BF" },
            },
          };
        });
      });
      worksheet["!cols"] = headers.map((header, index) => ({
        wch: Math.min(
          Math.max(
            String(header).length + 2,
            ...rows.map((row) => String(row[index] ?? "").length + 2),
            12
          ),
          48
        ),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Survey Responses");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      const filenameParts = [
        scopeLabel,
        "survey-responses",
        projectCode ? exportFilenamePart(projectCode) : null,
        isProjectScope ? null : exportFilenamePart(countryLabel),
        isVendorScope ? exportFilenamePart(vendorName) : null,
        timestamp,
      ].filter(Boolean);

      XLSX.writeFile(workbook, `${filenameParts.join("-")}.xlsx`);
    } catch (error) {
      window.alert(error.message || "Failed to export survey responses.");
    } finally {
      setExportLoading(false);
    }
  };

  if (!canViewProjects || !canViewSurveyResponses) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="body2" color="text">
            You do not have permission to view survey responses.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (PageLayout) {
    return (
      <PageLayout
        model={{
          applyFilters,
          branchFilter,
          canExportSurveyResponses,
          canSearchResponses,
          canViewClient,
          canViewCompletesSummary,
          canViewCountry,
          canViewIpMismatchSummary,
          canViewProjectCode,
          canViewResponseFilter,
          canViewRevenueSummary,
          canViewStatus,
          clientFilter,
          clearSelectValue: clearedSelectValue,
          countryFilter,
          endDateFilter,
          exportLoading,
          exportResponses,
          goToResponsePage,
          handleBranchFilterChange,
          handleShiftFilterChange,
          handleSubBranchFilterChange,
          handleVendorFilterChange,
          hasSingleVendorBranch,
          loading,
          pageInput,
          pagination,
          paginationEnd,
          paginationStart,
          paginationTotal,
          pidFilter,
          pidFilterOptions,
          projectCodeFilter,
          responseColumns,
          responseSummary,
          ridFilter,
          ridFilterOptions,
          searchInput,
          setClientFilter,
          setCountryFilter,
          setEndDateFilter,
          setPageInput,
          setPidFilter,
          setProjectCodeFilter,
          setRidFilter,
          setSearch,
          setSearchInput,
          setStartDateFilter,
          setStatus,
          setUserFilter,
          shiftFilter,
          showUserFilter,
          showVendorFilter,
          startDateFilter,
          status,
          statusOptions,
          subBranchFilter,
          userFilter,
          vendorBranchOptions,
          vendorClientOptions,
          vendorCountryOptions,
          vendorFilter,
          vendorProjectCodeOptions: projectCodeOptions,
          vendorShiftOptions,
          vendorSubBranchOptions,
          vendorUserOptions,
          vendorVendorOptions: countryVendorOptions,
          visibleResponses,
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={1}>
        <SoftBox display="flex" alignItems="center" gap={0.75} mb={0.75}>
          <SoftButton
            variant="gradient"
            color="info"
            size="small"
            onClick={goBack}
            sx={{ minWidth: "auto", px: 1.5, py: 0.55, textTransform: "none" }}
          >
            Go Back
          </SoftButton>
          <SoftTypography variant="h4" fontWeight="bold" color="dark" sx={{ fontSize: "0.875rem" }}>
            {pageTitle}
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox
            display="flex"
            alignItems={isCompactTableScope ? "stretch" : "center"}
            justifyContent={isCompactTableScope ? "flex-start" : "space-between"}
            flexWrap={isCompactTableScope ? "nowrap" : "wrap"}
            flexDirection={isCompactTableScope ? "column" : "row"}
            gap={0.75}
            px={1}
            py={0.6}
            sx={{ borderBottom: "1px solid #e9ecef" }}
          >
            <SoftBox minWidth={0}>
              <SoftTypography sx={{ fontSize: "12px", fontWeight: 700, color: "#1e293b" }}>
                {textOrDash(displayScopeText)}
              </SoftTypography>
            </SoftBox>
            <SoftBox
              display="flex"
              alignItems="center"
              gap={0.5}
              flexWrap={isCompactTableScope && !isVendorProjectResponseRoute ? "nowrap" : "wrap"}
              width={isCompactTableScope ? "100%" : "auto"}
              sx={{
                minWidth: 0,
                overflowX: isVendorProjectResponseRoute ? "visible" : "hidden",
              }}
            >
              {canViewStatus && canViewResponseFilter("status") && (
                <SoftBox sx={isCompactTableScope ? countryStatusFilterSx : compactFilterBoxSx}>
                  <SoftSelect
                    size="small"
                    compactMenu
                    value={status}
                    options={statusOptions}
                    onChange={(options) => setStatus(clearedSelectValue(options))}
                    isSearchable
                    isClearable
                    isMulti
                  />
                </SoftBox>
              )}
              {(isCompactTableScope || isVendorProjectResponseRoute) &&
                canViewProjectCode &&
                canViewResponseFilter("project_code") && (
                  <SoftBox sx={projectCountryFilterSx}>
                    <SoftSelect
                      key={`project-code-${projectCodeFilter?.value || "all"}`}
                      size="small"
                      compactMenu
                      placeholder="Project Code"
                      value={projectCodeFilter}
                      options={projectCodeOptions}
                      isLoading={filterOptionsLoading}
                      onChange={(option, actionMeta) =>
                        setProjectCodeFilter(clearedSelectValue(option, actionMeta))
                      }
                      isSearchable
                      isClearable
                      isMulti
                    />
                  </SoftBox>
                )}
              {isVendorProjectResponseRoute && (
                <>
                  {canViewResponseFilter("branch") && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`branch-${branchFilter?.value || "blank"}`}
                        size="small"
                        compactMenu
                        placeholder="Branch"
                        value={branchFilter}
                        options={vendorBranchOptions}
                        onChange={handleBranchFilterChange}
                        isSearchable
                        isClearable={!hasSingleVendorBranch}
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("sub_branch") && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`sub-branch-${branchFilter?.value || "no-branch"}-${
                          subBranchFilter?.value || "blank"
                        }`}
                        size="small"
                        compactMenu
                        placeholder="Sub Branch"
                        value={subBranchFilter}
                        options={vendorSubBranchOptions}
                        onChange={handleSubBranchFilterChange}
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("shift") && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`shift-${subBranchFilter?.value || "no-sub-branch"}-${
                          shiftFilter?.value || "blank"
                        }`}
                        size="small"
                        compactMenu
                        placeholder="Shift"
                        value={shiftFilter}
                        options={vendorShiftOptions}
                        onChange={handleShiftFilterChange}
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {showUserFilter && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`shift-user-${shiftFilter?.value || "no-shift"}-${
                          userFilter?.value || "blank"
                        }`}
                        size="small"
                        compactMenu
                        placeholder="Shift-wise User"
                        value={userFilter}
                        options={vendorUserOptions}
                        onChange={(option, actionMeta) =>
                          setUserFilter(clearedSelectValue(option, actionMeta))
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("country") && (
                    <SoftBox sx={countryStatusFilterSx}>
                      <SoftSelect
                        key={`country-${countryFilter?.value || "all"}`}
                        size="small"
                        compactMenu
                        placeholder="Country Code"
                        value={countryFilter}
                        options={vendorCountryOptions}
                        onChange={(option, actionMeta) =>
                          setCountryFilter(clearedSelectValue(option, actionMeta))
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("client") && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`client-${clientFilter?.value || "all"}`}
                        size="small"
                        compactMenu
                        placeholder="Client"
                        value={clientFilter}
                        options={vendorClientOptions}
                        onChange={(option, actionMeta) =>
                          setClientFilter(clearedSelectValue(option, actionMeta))
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("rid") && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        size="small"
                        placeholder="RID"
                        value={ridFilter}
                        options={ridFilterOptions}
                        onChange={(options) => setRidFilter(clearedSelectValue(options))}
                        onInputChange={(value, actionMeta) => {
                          if (actionMeta?.action === "input-change") setRidOptionSearch(value);
                        }}
                        isLoading={ridOptionsLoading}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue.trim().length < 2
                            ? "Type at least 2 characters"
                            : "No matching RID"
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("pid") && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        size="small"
                        placeholder="PID"
                        value={pidFilter}
                        options={pidFilterOptions}
                        onChange={(options) => setPidFilter(clearedSelectValue(options))}
                        onInputChange={(value, actionMeta) => {
                          if (actionMeta?.action === "input-change") setPidOptionSearch(value);
                        }}
                        isLoading={pidOptionsLoading}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue.trim().length < 2
                            ? "Type at least 2 characters"
                            : "No matching PID"
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("start_date") && (
                    <SoftBox sx={{ ...countryDateFilterSx, minWidth: 165 }}>
                      <SoftInput
                        size="small"
                        type="datetime-local"
                        value={startDateFilter}
                        onChange={(event) => setStartDateFilter(event.target.value)}
                        inputProps={{ title: "Start Date & Time" }}
                        sx={compactInputSx}
                      />
                    </SoftBox>
                  )}
                  {canViewResponseFilter("end_date") && (
                    <SoftBox sx={{ ...countryDateFilterSx, minWidth: 165 }}>
                      <SoftInput
                        size="small"
                        type="datetime-local"
                        value={endDateFilter}
                        onChange={(event) => setEndDateFilter(event.target.value)}
                        inputProps={{ title: "End Date & Time" }}
                        sx={compactInputSx}
                      />
                    </SoftBox>
                  )}
                </>
              )}
              {isVendorProjectAllScope && (
                <>
                  {showVendorFilter && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`all-vendor-${vendorFilter?.value || "all"}`}
                        size="small"
                        compactMenu
                        placeholder="Vendor"
                        value={vendorFilter}
                        options={countryVendorOptions}
                        onChange={handleVendorFilterChange}
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {showUserFilter && !isVendorProjectResponseRoute && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        size="small"
                        compactMenu
                        placeholder="User"
                        value={userFilter}
                        options={countryUserOptions}
                        onChange={(option, actionMeta) =>
                          setUserFilter(clearedSelectValue(option, actionMeta))
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                </>
              )}
              {isVendorScope && (
                <>
                  <SoftBox
                    sx={{
                      ...countryDateFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        (canViewCreatedAt || canViewUpdatedAt) &&
                        canViewResponseFilter("start_date")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftInput
                      size="small"
                      type="date"
                      value={startDateFilter}
                      onChange={(event) => setStartDateFilter(event.target.value)}
                      inputProps={{ title: "Start Date" }}
                      sx={compactInputSx}
                    />
                  </SoftBox>
                  <SoftBox
                    sx={{
                      ...countryDateFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        (canViewCreatedAt || canViewUpdatedAt) &&
                        canViewResponseFilter("end_date")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftInput
                      size="small"
                      type="date"
                      value={endDateFilter}
                      onChange={(event) => setEndDateFilter(event.target.value)}
                      inputProps={{ title: "End Date" }}
                      sx={compactInputSx}
                    />
                  </SoftBox>
                  <SoftBox
                    sx={{
                      ...linkTypeFilterSx,
                      display:
                        canViewLinkType && canViewResponseFilter("link_type") ? undefined : "none",
                    }}
                  >
                    <SoftSelect
                      key={`vendor-link-${linkTypeFilter?.value || "all"}`}
                      size="small"
                      compactMenu
                      placeholder="Link Type"
                      value={linkTypeFilter}
                      options={linkTypeOptions}
                      onChange={(option, actionMeta) =>
                        setLinkTypeFilter(clearedSelectValue(option, actionMeta))
                      }
                      isClearable
                      isSearchable
                      isMulti
                    />
                  </SoftBox>
                </>
              )}
              {isProjectScope && (
                <>
                  <SoftBox
                    sx={{
                      ...projectCountryFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        canViewCountry &&
                        canViewResponseFilter("country")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftSelect
                      key={`project-country-${countryFilter?.value || "all"}`}
                      size="small"
                      compactMenu
                      placeholder="Country"
                      value={countryFilter}
                      options={projectCountryOptions}
                      onChange={(option, actionMeta) =>
                        setCountryFilter(clearedSelectValue(option, actionMeta))
                      }
                      isSearchable
                      isClearable
                      isMulti
                    />
                  </SoftBox>
                  {showVendorFilter && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`project-vendor-${vendorFilter?.value || "all"}`}
                        size="small"
                        compactMenu
                        placeholder="Vendor"
                        value={vendorFilter}
                        options={countryVendorOptions}
                        onChange={handleVendorFilterChange}
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {showUserFilter && !isVendorProjectResponseRoute && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`project-link-${linkTypeFilter?.value || "all"}`}
                        size="small"
                        compactMenu
                        placeholder="User"
                        value={userFilter}
                        options={countryUserOptions}
                        onChange={(option, actionMeta) =>
                          setUserFilter(clearedSelectValue(option, actionMeta))
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  <SoftBox
                    sx={{
                      ...countryDateFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        (canViewCreatedAt || canViewUpdatedAt) &&
                        canViewResponseFilter("start_date")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftInput
                      size="small"
                      type="date"
                      value={startDateFilter}
                      onChange={(event) => setStartDateFilter(event.target.value)}
                      inputProps={{ title: "Start Date" }}
                      sx={compactInputSx}
                    />
                  </SoftBox>
                  <SoftBox
                    sx={{
                      ...countryDateFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        (canViewCreatedAt || canViewUpdatedAt) &&
                        canViewResponseFilter("end_date")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftInput
                      size="small"
                      type="date"
                      value={endDateFilter}
                      onChange={(event) => setEndDateFilter(event.target.value)}
                      inputProps={{ title: "End Date" }}
                      sx={compactInputSx}
                    />
                  </SoftBox>
                  <SoftBox
                    sx={{
                      ...linkTypeFilterSx,
                      display:
                        canViewLinkType && canViewResponseFilter("link_type") ? undefined : "none",
                    }}
                  >
                    <SoftSelect
                      key={`country-vendor-${vendorFilter?.value || "all"}`}
                      size="small"
                      compactMenu
                      placeholder="Link Type"
                      value={linkTypeFilter}
                      options={linkTypeOptions}
                      onChange={(option, actionMeta) =>
                        setLinkTypeFilter(clearedSelectValue(option, actionMeta))
                      }
                      isClearable
                      isSearchable
                      isMulti
                    />
                  </SoftBox>
                </>
              )}
              {isCountryScope && (
                <>
                  {showVendorFilter && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        key={`country-link-${linkTypeFilter?.value || "all"}`}
                        size="small"
                        compactMenu
                        placeholder="Vendor"
                        value={vendorFilter}
                        options={countryVendorOptions}
                        onChange={handleVendorFilterChange}
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  {showUserFilter && !isVendorProjectResponseRoute && (
                    <SoftBox sx={countryPersonFilterSx}>
                      <SoftSelect
                        size="small"
                        compactMenu
                        placeholder="User"
                        value={userFilter}
                        options={countryUserOptions}
                        onChange={(option, actionMeta) =>
                          setUserFilter(clearedSelectValue(option, actionMeta))
                        }
                        isSearchable
                        isClearable
                        isMulti
                      />
                    </SoftBox>
                  )}
                  <SoftBox
                    sx={{
                      ...countryDateFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        (canViewCreatedAt || canViewUpdatedAt) &&
                        canViewResponseFilter("start_date")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftInput
                      size="small"
                      type="date"
                      value={startDateFilter}
                      onChange={(event) => setStartDateFilter(event.target.value)}
                      inputProps={{ title: "Start Date" }}
                      sx={compactInputSx}
                    />
                  </SoftBox>
                  <SoftBox
                    sx={{
                      ...countryDateFilterSx,
                      display:
                        !isVendorProjectResponseRoute &&
                        (canViewCreatedAt || canViewUpdatedAt) &&
                        canViewResponseFilter("end_date")
                          ? undefined
                          : "none",
                    }}
                  >
                    <SoftInput
                      size="small"
                      type="date"
                      value={endDateFilter}
                      onChange={(event) => setEndDateFilter(event.target.value)}
                      inputProps={{ title: "End Date" }}
                      sx={compactInputSx}
                    />
                  </SoftBox>
                  <SoftBox
                    sx={{
                      ...linkTypeFilterSx,
                      display:
                        canViewLinkType && canViewResponseFilter("link_type") ? undefined : "none",
                    }}
                  >
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Link Type"
                      value={linkTypeFilter}
                      options={linkTypeOptions}
                      onChange={(option, actionMeta) =>
                        setLinkTypeFilter(clearedSelectValue(option, actionMeta))
                      }
                      isClearable
                      isSearchable
                      isMulti
                    />
                  </SoftBox>
                </>
              )}
              <SoftBox
                sx={{
                  ...(isCompactTableScope ? countrySearchFilterSx : { width: 220 }),
                  display: canSearchResponses ? undefined : "none",
                }}
              >
                <SoftInput
                  size="small"
                  placeholder={
                    isVendorProjectResponseRoute
                      ? isVendorActor
                        ? "Search RID, PID, user..."
                        : "Search RID, PID, vendor, user..."
                      : "Search RID, PID, vendor..."
                  }
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (!isCompactTableScope && event.key === "Enter") applyFilters();
                  }}
                  sx={compactInputSx}
                />
              </SoftBox>
              {!isCompactTableScope && canSearchResponses && (
                <SoftButton
                  color="info"
                  variant="gradient"
                  size="small"
                  onClick={applyFilters}
                  sx={{
                    ...(isCountryScope
                      ? countryFilterButtonSx
                      : {
                          minHeight: 26,
                          height: 26,
                          px: 1.3,
                          py: 0,
                          fontSize: "10px",
                          textTransform: "none",
                        }),
                  }}
                >
                  Search
                </SoftButton>
              )}
              {canExportSurveyResponses && (
                <SoftButton
                  color="info"
                  size="small"
                  onClick={() => exportResponses(scopeType)}
                  disabled={!visibleResponses.length || exportLoading}
                  sx={countryFilterButtonSx}
                >
                  <Icon sx={{ mr: 0.5, fontSize: "14px !important" }}>download</Icon>
                  {exportLoading ? "Exporting..." : "Export"}
                </SoftButton>
              )}
            </SoftBox>
          </SoftBox>

          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={0.5}
            px={1}
            py={0.25}
          >
            <SoftTypography sx={{ fontSize: "10px", color: "#64748b" }}>
              Showing {paginationStart} to {paginationEnd} of {paginationTotal} entries
            </SoftTypography>
            {(isVendorProjectResponseRoute ||
              canViewRevenueSummary ||
              canViewCompletesSummary ||
              canViewIpMismatchSummary) && (
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexGrow={1}
                flexWrap="wrap"
                gap={1.5}
                px={1}
              >
                {isVendorProjectResponseRoute && (
                  <SoftBox display="flex" alignItems="center" gap={0.35}>
                    <SoftTypography sx={{ fontSize: "10px", color: "#0ea5e9", fontWeight: 600 }}>
                      Count of Session IDs:
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: "10px", color: "#16a34a", fontWeight: 600 }}>
                      {summaryNumber(paginationTotal)}
                    </SoftTypography>
                  </SoftBox>
                )}
                {canViewRevenueSummary && (
                  <SoftBox display="flex" alignItems="center" gap={0.35}>
                    <SoftTypography sx={{ fontSize: "10px", color: "#0ea5e9", fontWeight: 600 }}>
                      Total Revenue:
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: "10px", color: "#16a34a", fontWeight: 600 }}>
                      {summaryNumber(responseSummary.total_revenue)}
                    </SoftTypography>
                  </SoftBox>
                )}
                {canViewCompletesSummary && (
                  <SoftBox display="flex" alignItems="center" gap={0.35}>
                    <SoftTypography sx={{ fontSize: "10px", color: "#0ea5e9", fontWeight: 600 }}>
                      Completes:
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: "10px", color: "#16a34a", fontWeight: 600 }}>
                      {summaryNumber(responseSummary.total_completes)}
                    </SoftTypography>
                  </SoftBox>
                )}
                {canViewIpMismatchSummary && (
                  <SoftBox display="flex" alignItems="center" gap={0.35}>
                    <SoftTypography sx={{ fontSize: "10px", color: "#0ea5e9", fontWeight: 600 }}>
                      IP Mismatches:
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: "10px", color: "#dc2626", fontWeight: 600 }}>
                      {summaryNumber(responseSummary.total_ip_mismatches)}
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            )}
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftPagination
                item
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => goToResponsePage(1)}
                sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
              >
                <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>first_page</Icon>
              </SoftPagination>
              <SoftPagination
                item
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => goToResponsePage(pagination.current_page - 1)}
                sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
              >
                <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>chevron_left</Icon>
              </SoftPagination>
              <SoftBox display="flex" alignItems="center" gap={0.4} mx={0.4}>
                <SoftBox
                  component="input"
                  type="text"
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value.replace(/\D/g, ""))}
                  onBlur={() => goToResponsePage(pageInput)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") goToResponsePage(pageInput);
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-label="Page number"
                  sx={{
                    width: 42,
                    minWidth: 42,
                    maxWidth: 42,
                    height: 22,
                    fontSize: "10px",
                    fontFamily: "inherit",
                    color: "#344767",
                    lineHeight: "20px",
                    textAlign: "center",
                    boxSizing: "border-box",
                    p: 0,
                    border: "1px solid #d2d6da",
                    borderRadius: "8px",
                    outline: "none",
                    "&:focus": {
                      borderColor: "#17c1e8",
                      boxShadow: "0 0 0 2px rgba(23, 193, 232, 0.18)",
                    },
                  }}
                />
                <SoftTypography sx={{ fontSize: "10px", color: "#64748b" }}>
                  / {pagination.last_page}
                </SoftTypography>
              </SoftBox>
              <SoftPagination
                item
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => goToResponsePage(pagination.current_page + 1)}
                sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
              >
                <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>chevron_right</Icon>
              </SoftPagination>
              <SoftPagination
                item
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => goToResponsePage(pagination.last_page)}
                sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
              >
                <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>last_page</Icon>
              </SoftPagination>
            </SoftBox>
          </SoftBox>

          {loading ? (
            <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight={220}>
              <CircularProgress size={28} />
            </SoftBox>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  minWidth: 0,
                  tableLayout: "fixed",
                }}
              >
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {responseColumns.map((column) => (
                      <TableCell key={column.key} sx={vendorHeaderCellSx}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleResponses.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(responseColumns.length, 1)}
                        align="center"
                        sx={tableBodyCellSx}
                      >
                        No survey responses found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleResponses.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          backgroundColor: row.ip_mismatch ? "#fff3bf" : "transparent",
                          boxShadow: row.ip_mismatch ? "inset 4px 0 0 #f59e0b" : "none",
                          "&.MuiTableRow-hover:hover": {
                            backgroundColor: row.ip_mismatch ? "#ffe082 !important" : "#f8fafc",
                          },
                          "& > td": {
                            backgroundColor: "inherit",
                          },
                        }}
                      >
                        {responseColumns.map((column) => {
                          const value = column.render(row);
                          const content = column.color ? (
                            <SoftTypography
                              variant="caption"
                              sx={{
                                color: column.color(row),
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: "9px",
                                fontWeight: 600,
                                lineHeight: 1.05,
                              }}
                            >
                              {textOrDash(value)}
                            </SoftTypography>
                          ) : column.truncate ? (
                            truncatedText(value)
                          ) : (
                            textOrDash(value)
                          );

                          return (
                            <TableCell
                              key={`${row.id}-${column.key}`}
                              sx={column.truncate ? userAgentCellSx : vendorBodyCellSx}
                              title={column.title ? column.title(row) : textOrDash(value)}
                            >
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </SoftBox>
      <Footer />
      {loading && <FlagLoader />}
    </DashboardLayout>
  );
}

ProjectSurveyResponses.propTypes = {
  forceVendorProjectResponseRoute: PropTypes.bool,
  forceAllResponseScope: PropTypes.bool,
  forceSurveyResponsesReportRoute: PropTypes.bool,
  PageLayout: PropTypes.elementType,
};

export default ProjectSurveyResponses;
